import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Tag, ArrowRight, Search } from "lucide-react";
import { NEWSLETTER_POSTS, normalizeNewsletterPost } from "../lib/site";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
const GITHUB_NEWSLETTER = "https://api.github.com/repos/AdigunQ/alisaafaat-foundation/contents/frontend/src/content/newsletter?ref=main";

function hasImage(post) {
  return typeof post?.image === "string" && post.image.trim().length > 0;
}

async function fetchLivePosts() {
  const response = await fetch(GITHUB_NEWSLETTER, { cache: "no-store", headers: { Accept: "application/vnd.github+json" } });
  if (!response.ok) return [];
  const files = await response.json();
  return Promise.all(files.filter((file) => file.name.endsWith(".json")).map(async (file) => normalizeNewsletterPost(await (await fetch(file.download_url, { cache: "no-store" })).json(), file.name.replace(/\.json$/, ""))));
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function NewsletterListPage() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [posts, setPosts] = useState(NEWSLETTER_POSTS);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    fetch(`${API}/newsletter`).then((r) => r.ok ? r.json() : []).then((remote) => {
      if (remote.length) setPosts(remote);
    }).catch(() => {});
    fetchLivePosts().then((remote) => { if (remote.length) setPosts(remote); }).catch(() => {});
  }, []);

  const categories = useMemo(
    () => ["All", ...new Set(posts.map((p) => p.category))],
    [posts],
  );

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      const q = query.toLowerCase();
      const matchesQ =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q);
      const matchesC = category === "All" || p.category === category;
      return matchesQ && matchesC;
    }).sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [query, category, posts]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <main className="bg-[#FAF5F0] min-h-screen">
      {/* HERO */}
      <section
        data-testid="newsletter-hero"
        className="relative pt-28 sm:pt-36 pb-12 sm:pb-20 bg-white border-b border-[#E5E0DA]"
      >
        <div className="absolute inset-0 islamic-pattern opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
          <Link
            to="/"
            data-testid="newsletter-back-link"
            className="inline-flex items-center gap-2 text-[#064E3B] text-xs sm:text-sm font-semibold tracking-wide hover:gap-3 transition-all"
          >
            <ArrowLeft size={14} /> Back to Home
          </Link>

          <div className="mt-6 text-[11px] uppercase tracking-[0.24em] font-semibold text-[#064E3B]">
            Newsletter
          </div>
          <div className="pill-divider mt-4" />
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05] text-[#111827] font-semibold">
            Stories from{" "}
            <span className="italic text-[#DC2626]">the field</span>.
          </h1>
          <p className="mt-5 sm:mt-6 max-w-2xl text-base sm:text-lg text-[#374151] leading-relaxed">
            Updates on our charity work, programme launches, beneficiary stories
            and reflections — straight from the Alisaafaat team.
          </p>

          {/* Search + filter */}
          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9ca3af]"
              />
              <input
                type="text"
                placeholder="Search posts…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="newsletter-search"
                className="w-full rounded-full border border-[#E5E0DA] bg-[#FAF5F0] pl-11 pr-4 py-3 text-sm text-[#111827] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#064E3B] focus:border-[#064E3B] transition-all"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`newsletter-filter-${c.toLowerCase()}`}
                  className={`rounded-full px-4 py-2 text-xs font-semibold border transition-all ${
                    category === c
                      ? "bg-[#064E3B] text-white border-[#064E3B]"
                      : "bg-white text-[#111827] border-[#E5E0DA] hover:border-[#064E3B]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* POSTS */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
          {filtered.length === 0 && (
            <div
              data-testid="newsletter-empty"
              className="rounded-3xl border border-dashed border-[#E5E0DA] bg-white py-16 text-center text-[#374151]"
            >
              <p className="font-serif-display text-2xl text-[#111827]">
                No posts match your search.
              </p>
              <p className="mt-2 text-sm">Try a different keyword or filter.</p>
            </div>
          )}

          {featured && (
            <Link
              to={`/newsletter/${featured.slug}`}
              data-testid="newsletter-featured"
              className="group block rounded-3xl overflow-hidden bg-white border border-[#E5E0DA] hover:shadow-xl transition-all"
            >
              <div className={hasImage(featured) ? "grid lg:grid-cols-2" : ""}>
                {hasImage(featured) && (
                  <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                    <img
                      src={featured.image}
                      alt={featured.title}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full bg-[#DC2626] text-white text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1.5">
                      Featured
                    </span>
                  </div>
                )}
                <div className="p-7 sm:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-4 text-xs text-[#374151]">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={13} /> {formatDate(featured.date)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Tag size={13} /> {featured.category}
                    </span>
                  </div>
                  <h2 className="font-serif-display text-3xl sm:text-4xl mt-4 leading-tight text-[#111827] font-semibold">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-[#374151] leading-relaxed text-[15px]">
                    {featured.excerpt}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[#064E3B] text-sm font-semibold group-hover:gap-3 transition-all">
                    Read full post <ArrowRight size={16} />
                  </span>
                </div>
              </div>
            </Link>
          )}

          {rest.length > 0 && (
            <div className="mt-6 sm:mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {rest.map((p) => (
                <PostCard key={p.slug} post={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subscribe band */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
          <div className="rounded-3xl bg-[#064E3B] text-white p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute inset-0 geo-overlay-light opacity-40" />
            <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FCD34D]">
                  Stay in the loop
                </div>
                <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 font-semibold leading-snug">
                  Be the first to hear our next update.
                </h3>
              </div>
              <Link
                to="/#contact"
                data-testid="newsletter-contact-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FCD34D] hover:bg-[#fbc818] text-[#064E3B] px-6 py-3 text-sm font-semibold transition-all hover:scale-[1.02]"
              >
                Get in touch <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function PostCard({ post }) {
  return (
    <Link
      to={`/newsletter/${post.slug}`}
      data-testid={`newsletter-post-${post.slug}`}
      className="group block rounded-3xl bg-white border border-[#E5E0DA] overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all"
    >
      {hasImage(post) && (
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
      )}
      <div className="p-6 sm:p-7">
        <div className="flex items-center gap-3 text-[11px] text-[#374151]">
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={12} /> {formatDate(post.date)}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#064E3B]/8 text-[#064E3B] px-2.5 py-0.5 font-semibold">
            {post.category}
          </span>
        </div>
        <h3 className="font-serif-display text-xl sm:text-2xl mt-3 leading-tight text-[#111827] font-semibold">
          {post.title}
        </h3>
        <p className="mt-3 text-[#374151] text-sm leading-relaxed line-clamp-3">
          {post.excerpt}
        </p>
        <span className="mt-5 inline-flex items-center gap-2 text-[#064E3B] text-xs font-semibold group-hover:gap-3 transition-all">
          Read more <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}

export function NewsletterPostPage() {
  const { slug } = useParams();
  const [remotePost, setRemotePost] = useState(null);
  const fallbackPost = NEWSLETTER_POSTS.find((p) => p.slug === slug);
  const post = remotePost || fallbackPost;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    fetch(`${API}/newsletter`).then((r) => r.ok ? r.json() : []).then((items) => setRemotePost(items.find((p) => p.slug === slug) || null)).catch(() => {});
    fetchLivePosts().then((items) => setRemotePost(items.find((p) => p.slug === slug) || null)).catch(() => {});
  }, [slug]);

  if (!post) {
    return (
      <main className="bg-[#FAF5F0] min-h-screen pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <h1 className="font-serif-display text-4xl text-[#111827] font-semibold">
            Post not found
          </h1>
          <p className="mt-4 text-[#374151]">
            The post you're looking for may have moved or doesn't exist.
          </p>
          <Link
            to="/newsletter"
            className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#064E3B] text-white px-6 py-3 text-sm font-semibold"
          >
            <ArrowLeft size={14} /> Back to Newsletter
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#FAF5F0] min-h-screen">
      <article className="pt-28 sm:pt-36 pb-16 sm:pb-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-8">
          <Link
            to="/newsletter"
            data-testid="post-back-link"
            className="inline-flex items-center gap-2 text-[#064E3B] text-xs sm:text-sm font-semibold tracking-wide hover:gap-3 transition-all"
          >
            <ArrowLeft size={14} /> All posts
          </Link>

          <div className="mt-6 flex items-center gap-3 text-xs text-[#374151]">
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={13} /> {formatDate(post.date)}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#064E3B]/8 text-[#064E3B] px-3 py-1 font-semibold">
              {post.category}
            </span>
          </div>

          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.1] text-[#111827] font-semibold">
            {post.title}
          </h1>

          {hasImage(post) && (
            <div className="mt-8 rounded-3xl overflow-hidden border border-[#E5E0DA]">
              <img
                src={post.image}
                alt={post.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>
          )}

          <div className="mt-8 sm:mt-10 prose prose-lg max-w-none">
            <p
              data-testid="post-excerpt"
              className="font-serif-display text-xl sm:text-2xl text-[#111827] italic leading-snug"
            >
              {post.excerpt}
            </p>
            <div
              data-testid="post-body"
              className="mt-6 text-[#374151] text-[17px] leading-[1.8] space-y-5"
            >
              {String(post.body || "")
                .split(/\n\s*\n/)
                .filter((paragraph) => paragraph.trim())
                .map((paragraph, index) => (
                  <p key={index} className="whitespace-pre-line">{paragraph.trim()}</p>
                ))}
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-[#E5E0DA] flex flex-col sm:flex-row gap-3">
            <Link
              to="/#donate"
              data-testid="post-donate-cta"
              className="cta-shimmer inline-flex items-center justify-center gap-2 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white px-7 py-3.5 text-sm font-semibold transition-all hover:scale-[1.02]"
            >
              Support this work
            </Link>
            <Link
              to="/newsletter"
              data-testid="post-back-bottom"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-[#E5E0DA] hover:border-[#064E3B] text-[#111827] hover:text-[#064E3B] px-7 py-3.5 text-sm font-semibold transition-colors"
            >
              <ArrowLeft size={14} /> Back to newsletter
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}
